import type { RequestOptions } from '@/services/apiService';
import { UserService } from '@/services/masters/user/userService';
import { userActions } from '@/store/slices/masters/user/userApiSlice';
import type { UserInfo } from '@/types/authresponse';
import type { StoreBaseResponse } from '@/types/types';
import type { NullableAddUserPayload } from '@/types/userlist';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { produce } from 'immer';
const { startLoading, saveFetchedData, dataFetchedWithError, stopLoading } =
  userActions;
const service = new UserService();

const get = (
  action: string,
  type: string,
  apiCall: (params?: RequestOptions['params']) => Promise<StoreBaseResponse>,
) =>
  createAsyncThunk(
    action,
    async (params: RequestOptions['params'], thunkApi) => {
      thunkApi.dispatch(startLoading(type));
      try {
        const response = await apiCall(params);
        const responseData = response.data;
        if (responseData) {
          thunkApi.dispatch(
            saveFetchedData({
              type,
              data: responseData,
              message: response.message,
            }),
          );
        }
        if (response.error) {
          thunkApi.dispatch(
            dataFetchedWithError({ type, message: response.message }),
          );
        }
      } catch {
        thunkApi.dispatch(stopLoading({ type }));
      }
    },
  );

export const getAuthenticatedUserData = createAsyncThunk(
  'user/userData',
  async (_, thunkApi) => {
    const type = 'authUserData';
    thunkApi.dispatch(startLoading(type));
    try {
      const response = await service.getUserData();
      const responseData = response.data as UserInfo;

      if (responseData) {
        thunkApi.dispatch(
          userActions.saveAuthUserData(
            produce(responseData, (draft: UserInfo) => {
              if (draft?.tenant?.settings?.currency?.toLowerCase() === 'yen') {
                if (draft.tenant.settings.supported_currencies) {
                  draft.tenant.settings.supported_currencies =
                    draft.tenant.settings.supported_currencies.map((item) => {
                      return item;
                    });
                }
              }
            }),
          ),
        );
      }
      if (response.error) {
        thunkApi.dispatch(
          dataFetchedWithError({ type, message: response.message }),
        );
      }
    } catch {
      thunkApi.dispatch(stopLoading({ type }));
    }
  },
);

export const getUserData = createAsyncThunk(
  'user/userDataForEdit',
  async (params: RequestOptions['params'], thunkApi) => {
    const type = 'userData';
    thunkApi.dispatch(startLoading(type));
    try {
      const response = await service.getUserDataForEdit(params);
      const responseData = response.data;

      if (responseData) {
        thunkApi.dispatch(
          saveFetchedData({
            type,
            data: responseData,
            message: response.message,
          }),
        );
      }
      if (response.error) {
        thunkApi.dispatch(
          dataFetchedWithError({ type, message: response.message }),
        );
      }
    } catch {
      thunkApi.dispatch(stopLoading({ type }));
    }
  },
);

export const getUserList = createAsyncThunk(
  'user/userList',
  async (params: RequestOptions['params'], thunkApi) => {
    const type = 'userList';

    thunkApi.dispatch(startLoading(type));
    try {
      const response = await service.getUserList(params);
      const responseData = response.data;
      if (responseData) {
        thunkApi.dispatch(
          saveFetchedData({
            type,
            data: responseData,
            message: response.message,
          }),
        );
      }
      if (response.error) {
        thunkApi.dispatch(
          dataFetchedWithError({ type, message: response.message }),
        );
      }
    } catch {
      thunkApi.dispatch(stopLoading({ type }));
    }
  },
);

export const getDepartments = get(
  'user/departments',
  'departments',
  service.getDepartments.bind(service),
);

export const postAddUser = createAsyncThunk(
  'user/postAddUser',
  async (
    params: {
      body: NullableAddUserPayload;
      awsBody: { email: string; password: string } | null;
      id: number | undefined;
    },
    thunkApi,
  ) => {
    const type = 'postAddUser';
    thunkApi.dispatch(startLoading(type));
    try {
      if (!params.id && params.awsBody) {
        // code block to run only when adding new user
        const aws_response = await service.addUserToAWS(params.awsBody);
        const aws_user_id = aws_response?.data?.userId;
        if (aws_response.error) {
          thunkApi.dispatch(
            dataFetchedWithError({ type, message: aws_response.message }),
          );
          return;
        }
        if (aws_user_id) {
          params.body.aws_user_id = aws_user_id;
        }
      }
      const response = await service.addUser(params.body, params.id);
      if (!response.error) {
        thunkApi.dispatch(
          saveFetchedData({
            type,
            data: response.data,
            message: response.message,
          }),
        );
        return {
          success: true,
          data: response.data,
          message: response.message,
        };
      }
      if (response.error) {
        thunkApi.dispatch(
          dataFetchedWithError({ type, message: response.message }),
        );
        return thunkApi.rejectWithValue({
          success: false,
          message: response.message,
        });
      }
    } catch (error) {
      thunkApi.dispatch(stopLoading({ type }));
      return thunkApi.rejectWithValue({
        success: false,
        message: error,
      });
    }
  },
);

export const getUsers = get(
  'user/users',
  'users',
  service.getAllUsers.bind(service),
);

export const uploadUserProfile = createAsyncThunk(
  'user/uploadProfile',
  async (params: { formData: FormData; id?: number }, thunkApi) => {
    const type = 'uploadProfile';
    thunkApi.dispatch(startLoading(type));

    try {
      const response = await service.uploadProfile(params.formData, params.id);

      if (!response.error) {
        thunkApi.dispatch(
          saveFetchedData({
            type,
            data: response.data,
            message: response.message,
          }),
        );
        return response;
      }

      return thunkApi.rejectWithValue(response.message);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      thunkApi.dispatch(stopLoading({ type }));
      return thunkApi.rejectWithValue(error?.message || 'Upload failed');
    }
  },
);
